import { Tab, Tabs, Spinner } from "@heroui/react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useParams, Outlet, useNavigate, useLocation } from "react-router-dom";

import { useAuth } from "@wc/domain/authn";

import OrgProvider from "../contexts/org-context";
import useOrg from "../hooks/use-org";

/**
 * @param {object} props The component props
 * @param {React.ReactNode} props.children The children to render
 * @returns {React.ReactElement} Layout wrapped in OrgProvider using user's active org
 */
export function ActiveOrgLayout({ children }) {
  const authData = useAuth();

  if (!authData.isLoggedIn()) {
    return <>{children}</>;
  }

  const user = authData.getCurrentUser();
  const activeOrg = user.activeOrganization;

  if (!activeOrg?.name) {
    return (
      <div className="flex justify-center p-8">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <OrgProvider orgId={activeOrg.id} initialOrg={activeOrg} userId={user.id}>
      {children}
    </OrgProvider>
  );
}

/**
 * @returns {React.ReactElement} Inside the provider so we can get org information
 */
function OrgSettingsLayoutInternal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const orgData = useOrg();

  const validTabKeys = ["overview", "info", "members", "teams"];

  // Determine which org tab should be selected
  // If we're viewing a team detail page, keep "teams" selected
  const pathSegments = location.pathname.split("/");
  const teamsIndex = pathSegments.indexOf("teams");
  const isInTeamDetail =
    teamsIndex !== -1 && pathSegments.length > teamsIndex + 1;

  let defaultSelectedKey = "overview";
  if (isInTeamDetail) {
    defaultSelectedKey = "teams";
  } else {
    const currentPath = pathSegments.pop();
    if (validTabKeys.includes(currentPath)) {
      defaultSelectedKey = currentPath;
    }
  }

  if (!orgData.org) {
    return (
      <div className="mt-8 flex justify-center">
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <div>
      <h1 className="mb-4 text-4xl font-bold tracking-tight">
        {t("org.settingsTitle")}{" "}
        <span className="text-primary">{orgData?.org.displayName}</span>
      </h1>
      <Tabs
        variant="bordered"
        color="primary"
        selectedKey={defaultSelectedKey}
        onSelectionChange={(key) => navigate(`/orgs/${orgData?.org.id}/${key}`)}
      >
        {validTabKeys.map((tk) => {
          return <Tab key={tk} title={t(`org.tab-${tk}`)} />;
        })}
      </Tabs>
      <div className="mt-4">
        <Outlet />
      </div>
    </div>
  );
}

/**
 * This is a layout that should always be used in the context of an organization.
 * This expects there to be an orgId in the url parameters.
 * @returns {React.ReactElement} The layout for the org settings pages
 */
export function OrgSettingsLayout() {
  const { orgId } = useParams();
  const authData = useAuth();

  return (
    <OrgProvider orgId={orgId} userId={authData.getCurrentUser().id}>
      <OrgSettingsLayoutInternal />
    </OrgProvider>
  );
}
